import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { formatDate } from '../../../utils/Utils';
import { employeeDisplayName, futureStartDate } from '../employeeOption';
import { POSITION_MODES, NEW_MODE_MAX_HEADCOUNT, positionModeOf } from '../requisitionActions';
import { positionAsync } from '../../positions/positionPicker';

// Kadro talebi FORMU — şema, ön doldurma ve gövde üreticisi. Liste sayfası
// (useAtsRequisitionsApi) ile Organizasyon Şeması (/org-chart) AYNI formu kullanır; bu yüzden
// liste çekmeyen ayrı bir hook'tur (Organizasyon Şeması'ndaki yöneticinin AT.Read'i olmayabilir,
// `GET /v1/ats-requisitions` ona 403 döner).
//
// Seçicilerin uçları (pozisyon, ünvan, departman, lokasyon, çalışan) EM.Read ister — EM
// varsayılan modül, AT yetkisi OLMAYAN kullanıcıda da çalışır.

// BE enum values — see Models/AtsRequisition.cs
export const CONTRACT_TYPES = ['permanent_full_time', 'permanent_part_time', 'fixed_term', 'internship', 'contractor'];
export const EMPLOYMENT_TYPES = ['full_time', 'part_time', 'shift'];
export const REMOTE_TYPES = ['onsite', 'hybrid', 'remote'];

// Builds the BE-compatible payload from form state. The form keeps optional
// numerics as empty strings; we coerce them to null so EF serialization
// doesn't reject the request.
//
// Kadro ↔ pozisyon modeli: `existing` → yalnız positionId, kişi sayısı 1 (sunucu da
// 1'e zorlar); `new` → parentPositionId + titleId + kişi sayısı, positionId NULL
// (pozisyonlar onayda açılır); `legacy` → eski alanlar AYNEN.
// Mod yoksa: yeni kayıtta `existing`, güncellemede `legacy` (göç öncesi satır).
// Bant hedefi (spec 2026-10-27 §7.3): mevcut koltuk modunda pozisyon; yeni modda ünvan + üst pozisyon (üst pozisyon
// zorunlu alandır ve yetkisiz yöneticinin kapsamı ondan denetlenir — eksikken istek atılmaz, 403 uyarısı çıkmaz).
const bandTarget = (fd) => {
  if (positionModeOf(fd) === 'new') {
    return fd.titleId && fd.parentPositionId ? { titleId: fd.titleId, parentPositionId: fd.parentPositionId } : null;
  }
  return fd.positionId ? { positionId: fd.positionId } : null;
};

export function buildRequisitionPayload(formData, { isUpdate = false, ownerLocked = false } = {}) {
  const mode = formData.positionMode || (isUpdate ? 'legacy' : positionModeOf(formData));
  const isNew = mode === 'new';
  return {
    positionMode: mode,
    justificationText: formData.justificationText || null,
    departmentId: formData.departmentId || null,
    positionId: isNew ? null : (formData.positionId || null),
    parentPositionId: isNew ? (formData.parentPositionId || null) : null,
    titleId: isNew ? (formData.titleId || null) : null,
    // Sahibi kilitli bağlamda (Ekibimin Şeması, AT.Write'sız kullanıcı) HM GÖNDERİLMEZ: sunucu
    // ayrıcalıksız çağıranı zaten sahip yapar (owner capture); pozisyondan türeyen HM
    // gövdeye sızıp yanlış izlenim vermesin.
    ...(ownerLocked ? {} : { hiringManagerUserId: formData.hiringManagerUserId }),
    hrBpUserId: formData.hrBpUserId || null,
    requestedHeadcount: mode === 'existing'
      ? 1
      : (formData.requestedHeadcount ? parseInt(formData.requestedHeadcount, 10) : 1),
    minSalary: formData.minSalary === '' || formData.minSalary == null ? null : parseFloat(formData.minSalary),
    maxSalary: formData.maxSalary === '' || formData.maxSalary == null ? null : parseFloat(formData.maxSalary),
    salaryCurrency: formData.salaryCurrency || null,
    contractType: formData.contractType || null,
    employmentType: formData.employmentType || null,
    location: formData.location || null,
    remoteType: formData.remoteType || null,
  };
}

// `writable` = AT.Write / admin: İK İş Ortağı seçicisi yalnız ona görünür (BP-13).
// `ownerLocked` = çağıran talebin sahibi olmak ZORUNDA (AT.Write'sız, Ekibimin Şeması):
// İşe Alım Yöneticisi seçicisi yerine not gösterilir, İK İş Ortağı hiç gösterilmez ve
// gövdeye HM yazılmaz — sunucu ayrıcalıksız çağıranı sahip yapar.
export function useRequisitionForm({ writable = false, ownerLocked = false } = {}) {
  const { t, i18n } = useTranslation();
  const i18nLocale = i18n?.language || 'tr';
  // Kademe bandı tutarları yalnız İK / işe alıma (EM.Write, AT.Write, admin — sunucuyla aynı karar; spec §6.4, kullanıcı
  // kararı 2026-10-27). ATS yetkisiz yönetici talep açar ama öneri istenmez; sunucu da ona boş döner.
  const { canWrite } = useAuth() || {};
  const canSeeBands = writable || Boolean(canWrite?.('EM'));

  const contractOptions = useMemo(
    () => CONTRACT_TYPES.map((c) => ({ id: c, name: t(`ats.requisition.contractType.${c}`, { defaultValue: c }) })),
    [t],
  );
  const employmentOptions = useMemo(
    () => EMPLOYMENT_TYPES.map((e) => ({ id: e, name: t(`ats.requisition.employmentType.${e}`, { defaultValue: e }) })),
    [t],
  );
  const remoteOptions = useMemo(
    () => REMOTE_TYPES.map((r) => ({ id: r, name: t(`ats.requisition.remoteType.${r}`, { defaultValue: r }) })),
    [t],
  );
  // Common ISO 4217 codes — most tenants only need TRY/USD/EUR; the rest are
  // here so the form doesn't force a custom-currency tenant to pick "TRY" by accident.
  const currencyOptions = useMemo(
    () => ['TRY', 'USD', 'EUR', 'GBP'].map((c) => ({ id: c, name: c })),
    [],
  );

  // /v1/employees is the canonical person-lookup endpoint across the codebase
  // (puantaj, work-plans, asset-assignments etc. all use it). The BE columns
  // HiringManagerUserId / HrBpUserId are plain Guid columns with no FK
  // constraint, so passing Employee.Id flows through cleanly.
  // Henüz işe başlamamış kişiler (preboarding, EmploymentDate ileride) listeden
  // ÇIKARILMAZ ama "(Başlangıç: …)" etiketiyle ayırt edilir — müşteri geri
  // bildirimi 2026-08; isim çakışmalarını da (aynı adlı iki kişi) çözer.
  const optionName = useCallback((e) => {
    const base = employeeDisplayName(e);
    const start = futureStartDate(e);
    return start
      ? `${base} (${t('ats.requisition.startsOn')}: ${formatDate(start, i18n.language)})`
      : base;
  }, [t, i18n.language]);

  const userSearch = useCallback(async (q) => {
    const params = q ? { 'fullName[like]': q, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
    const r = res.data?.data;
    const items = Array.isArray(r) ? r : (r?.data || []);
    return items.map((e) => ({ id: e.id, name: optionName(e), profilePicture: e.profilePicture }));
  }, [optionName]);

  const userGetById = useCallback(async (id) => {
    try {
      const res = await api.get(`/v1/employees/${id}`);
      const e = res.data?.data || {};
      return { id, name: optionName(e) || id, profilePicture: e.profilePicture };
    } catch {
      return { id, name: '', profilePicture: null };
    }
  }, [optionName]);

  const userAsyncSelect = useCallback(() => ({ onSearch: userSearch, onGetById: userGetById }), [userSearch, userGetById]);

  // Görev ayrılığı: HR BP seçili İşe Alım Yöneticisi olamaz (backend 400 ile reddeder) — HR BP
  // seçicisi HM'yi listelemez. GenericFormDrawer onSearch'e form değerlerini VERMEZ; seçili HM
  // bir ref'te izlenir: (1) HM alanının değişim kancası `deriveFields` (yama döndürmez),
  // (2) pozisyondan türeyen HM, (3) düzenleme/yeni açılışında sayfanın `noteHiringManager`
  // çağrısı (initialValues drawer'a doğrudan girer, kanca tetiklenmez).
  const hiringManagerRef = useRef(null);
  const noteHiringManager = useCallback((id) => { hiringManagerRef.current = id || null; }, []);
  const hrBpSearch = useCallback(async (q) => {
    const options = await userSearch(q);
    return options.filter((o) => o.id !== hiringManagerRef.current);
  }, [userSearch]);

  // Pozisyon seçicisi (mevcut pozisyon + yeni pozisyonların üst pozisyonu). Kod ya da
  // bağlı ünvan adıyla arar — BE filtresi q[like] ikisini OR'lar.
  // Pozisyon seçicileri (mevcut pozisyon + yeni pozisyonların üstü) ortak zengin seçiciyi
  // kullanır: ünvan · departman · oturan kişi (ya da Boş) · kod; ünvan, departman, kişi adı
  // veya kodla aranır (../../positions/positionPicker). Doğru üst pozisyonu bulmanın en
  // kolay yolu çoğu zaman oturan kişinin adıdır ("Ahmet Bey'e bağlı").
  const positionSearch = positionAsync.onSearch;
  const positionGetById = positionAsync.onGetById;

  // Picking a Position auto-fills the Department (the position's home
  // department) AND the Hiring Manager (üst pozisyonun aktif sahibi — org
  // ağacının ima ettiği yönetici; müşteri geri bildirimi: kadro talebindeki
  // yönetici işe alımda adayın yöneticisi olmalı, bu ancak seçilen pozisyon
  // o yöneticiye hiyerarşik bağlıysa doğal olarak sağlanır). The user can
  // still override both. BE also derives Department as a fallback, and
  // enforces it at "Onaya Gönder".
  const derivePositionFields = useCallback(async (positionId) => {
    if (!positionId) return {};
    const patch = {};
    try {
      const res = await api.get(`/v1/positions/${positionId}`);
      const p = res.data?.data || {};
      if (p.departmentId) patch.departmentId = p.departmentId;
    } catch { /* interceptor */ }
    try {
      const res = await api.get(`/v1/positions/${positionId}/manager`);
      const m = res.data?.data;
      // Çözümlenemezse (kök pozisyon / boş üst pozisyon) mevcut seçim korunur.
      if (m?.employeeId) {
        patch.hiringManagerUserId = m.employeeId;
        // Yama drawer'a doğrudan girer (HM alanının kancası tetiklenmez) — ref burada güncellenir.
        noteHiringManager(m.employeeId);
      }
    } catch { /* interceptor */ }
    return patch;
  }, [noteHiringManager]);

  // Kademe bandı önerisi (spec 2026-10-27 §7.3): yalnız BOŞ maaş alanlarını doldurur (kullanıcının yazdığına
  // dokunmaz) ve `salaryBandHint` ile alan açıklamasına "Kademe Bandı: X – Y" yazar. salaryBandHint
  // gönderilmez (buildRequisitionPayload alanları tek tek seçer). ÖNCEKİ öneriden gelen ve kullanıcının değiştirmediği
  // değer de "boş" sayılır: pozisyon/ünvan ya da para birimi değişince yeni bantla yenilenir, bant kalmadıysa temizlenir
  // (eski bandın sayısı yeni ipucunun yanında kalmasın — son inceleme M7).
  const deriveBandFields = useCallback(async (target, formData = {}) => {
    if (!canSeeBands) return {};
    const prev = formData.salaryBandHint;
    const empty = (v) => v === '' || v == null;
    const fromPrev = (v, prevValue) => prev != null && !empty(v) && prevValue != null && Number(v) === Number(prevValue);
    try {
      const params = { ...target };
      if (formData.salaryCurrency) params.currency = formData.salaryCurrency;
      const res = await api.get('/v1/ats-requisitions/band-suggestion', { params });
      const band = res.data?.status ? res.data.data : null;
      const patch = { salaryBandHint: band ? { min: band.min, max: band.max, currency: band.currency } : undefined };
      if (band) {
        if (empty(formData.minSalary) || fromPrev(formData.minSalary, prev?.min)) patch.minSalary = band.min;
        if (empty(formData.maxSalary) || fromPrev(formData.maxSalary, prev?.max)) patch.maxSalary = band.max;
        if (empty(formData.salaryCurrency)) patch.salaryCurrency = band.currency;
      } else if (prev) {
        if (fromPrev(formData.minSalary, prev.min)) patch.minSalary = '';
        if (fromPrev(formData.maxSalary, prev.max)) patch.maxSalary = '';
      }
      return patch;
    } catch {
      return {};
    }
  }, [canSeeBands]);


  const derivePositionAndBand = useCallback(async (positionId, formData) => {
    const patch = await derivePositionFields(positionId);
    if (!positionId) return { ...patch, salaryBandHint: undefined };
    return { ...patch, ...(await deriveBandFields({ positionId }, formData)) };
  }, [derivePositionFields, deriveBandFields]);

  // "new" mod: öneri ünvandan; yetkisiz yöneticinin kapsamı üst pozisyondan denetlenir. Ünvan, üst pozisyon ya da
  // para birimi değişince aynı hedef yeniden sorulur.
  const deriveTitleBand = useCallback(async (titleId, formData = {}) => {
    const target = bandTarget({ ...formData, positionMode: 'new', titleId });
    return target ? deriveBandFields(target, formData) : { salaryBandHint: undefined };
  }, [deriveBandFields]);

  const deriveParentBand = useCallback(async (parentPositionId, formData = {}) => {
    const target = bandTarget({ ...formData, positionMode: 'new', parentPositionId });
    return target ? deriveBandFields(target, formData) : undefined;
  }, [deriveBandFields]);

  const deriveCurrencyBand = useCallback(async (currency, formData = {}) => {
    const target = bandTarget(formData);
    return target ? deriveBandFields(target, { ...formData, salaryCurrency: currency }) : undefined;
  }, [deriveBandFields]);

  // Derin bağlantı ön doldurması (Ekibim / org şeması → "İşe alım talebi aç"):
  //   ?new=1&mode=existing&positionId=…  |  ?new=1&mode=new&parentPositionId=…
  // initialValues drawer'a doğrudan girdiği için pozisyon kancası TETİKLENMEZ —
  // türeyen departman + yönetici burada bir kez çözülür.
  const buildPrefill = useCallback(async ({ mode, positionId, parentPositionId }) => {
    if (mode === 'new') {
      return { positionMode: 'new', parentPositionId: parentPositionId || null };
    }
    const derived = positionId ? await derivePositionAndBand(positionId, {}) : {};
    return { positionMode: 'existing', positionId: positionId || null, ...derived };
  }, [derivePositionAndBand]);

  const modeOptions = useMemo(
    () => POSITION_MODES.map((m) => ({ id: m, name: t(`ats.requisition.positionMode.${m}`) })),
    [t],
  );

  const objectSchema = useMemo(() => [
    // Talep türü: mevcut (boş / sahibi ayrılan) pozisyon mu, yeni pozisyon(lar) mı.
    // Düzenlemede mod DEĞİŞTİRİLEMEZ (seçici gizlenir, yerine kilit notu); legacy
    // satırda hiç görünmez — eski alanlar aynen.
    {
      name: 'positionMode', label: t('ats.requisition.positionMode.label'), type: 'select', grid: 12,
      required: true, defaultValue: 'existing', options: modeOptions, idKey: 'id', labelKey: 'name',
      hidden: (fd) => !!fd.id || positionModeOf(fd) === 'legacy',
    },
    {
      name: 'positionModeLockedExisting', type: 'notice', grid: 12,
      content: t('ats.requisition.positionModeLocked.existing'),
      hidden: (fd) => !(fd.id && fd.positionMode === 'existing'),
    },
    {
      name: 'positionModeLockedNew', type: 'notice', grid: 12,
      content: t('ats.requisition.positionModeLocked.new'),
      hidden: (fd) => !(fd.id && fd.positionMode === 'new'),
    },
    {
      name: 'positionId', label: t('ats.requisition.positionId'), type: 'asyncSelect', grid: 6, required: true,
      onSearch: positionSearch,
      onGetById: positionGetById,
      renderOption: positionAsync.renderOption,
      searchPlaceholder: t('position.pickerSearchPlaceholder'),
      hidden: (fd) => positionModeOf(fd) === 'new',
      description: (fd) => (positionModeOf(fd) === 'existing' ? t('ats.requisition.positionVacantHint') : undefined),
      derives: ['departmentId', 'hiringManagerUserId', 'minSalary', 'maxSalary', 'salaryCurrency'],
      deriveFields: derivePositionAndBand,
    },
    {
      name: 'parentPositionId', label: t('ats.requisition.parentPositionId'), type: 'asyncSelect', grid: 6, required: true,
      onSearch: positionSearch,
      onGetById: positionGetById,
      renderOption: positionAsync.renderOption,
      searchPlaceholder: t('position.pickerSearchPlaceholder'),
      hidden: (fd) => positionModeOf(fd) !== 'new',
      description: t('ats.requisition.parentPositionHint'),
      derives: ['minSalary', 'maxSalary', 'salaryCurrency'],
      deriveFields: deriveParentBand,
    },
    {
      // GenericFormDrawer asyncSelect YALNIZ onSearch/onGetById okur (asyncConfig ölü alan).
      name: 'titleId', label: t('ats.requisition.titleId'), type: 'asyncSelect', grid: 6, required: true,
      onSearch: async (q) => {
        const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
        const res = await api.get('/v1/titles', { params });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        return items.map((x) => ({ id: x.id, name: x.name }));
      },
      onGetById: async (id) => {
        const res = await api.get(`/v1/titles/${id}`);
        const x = res.data?.data || {};
        return { id, name: x.name || '' };
      },
      hidden: (fd) => positionModeOf(fd) !== 'new',
      derives: ['minSalary', 'maxSalary', 'salaryCurrency'],
      deriveFields: deriveTitleBand,
    },
    {
      name: 'departmentId', label: t('ats.requisition.departmentId'), type: 'asyncSelect', grid: 6,
      onSearch: async (q) => {
        const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
        const res = await api.get('/v1/departments', { params });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        return items.map((d) => ({ id: d.id, name: d.name }));
      },
      onGetById: async (id) => {
        const res = await api.get(`/v1/departments/${id}`);
        const d = res.data?.data || {};
        return { id, name: d.name || '' };
      },
      description: (fd) => (positionModeOf(fd) === 'new' ? t('ats.requisition.departmentFromParentHint') : undefined),
    },
    {
      name: 'hiringManagerUserId', label: t('ats.requisition.hiringManagerUserId'),
      type: 'asyncSelect', grid: 6, required: true,
      ...userAsyncSelect(),
      // Değişim kancası: seçilen HM'yi HR BP seçicisi için not eder; yama döndürmez.
      deriveFields: async (id) => { noteHiringManager(id); return undefined; },
    },
    {
      // Sahibi kilitli bağlam: seçici yerine "İşe alım yöneticisi sizsiniz" notu.
      name: 'hiringManagerIsYou', type: 'notice', grid: 12,
      content: t('ats.requisition.hiringManagerIsYou'),
    },
    {
      name: 'hrBpUserId', label: t('ats.requisition.hrBpUserId'),
      type: 'asyncSelect', grid: 6,
      ...userAsyncSelect(),
      onSearch: hrBpSearch,
    },
    {
      // `existing` modda GÖRÜNMEZ — bir pozisyonda en fazla 1 kişi, gövdede 1 gider.
      name: 'requestedHeadcount', label: t('ats.requisition.requestedHeadcount'), type: 'number', grid: 6,
      required: true, min: 1, max: NEW_MODE_MAX_HEADCOUNT, step: 1,
      hidden: (fd) => positionModeOf(fd) === 'existing',
      description: (fd) => (positionModeOf(fd) === 'new' ? t('ats.requisition.headcountNewHint') : undefined),
    },
    {
      // Şube/Lokasyon — same concept across the app. The BE column is a free
      // string, so we send the location *name* (not id) once selected. This
      // keeps the existing Location filter on the requisitions list (text [like])
      // working without schema changes.
      // Müşteri isteği: lokasyon + sözleşme tipi + istihdam tipi + çalışma
      // modeli zorunlu — kariyer sayfasında adaya gösterilen alanlar. BE de
      // "Onaya Gönder" anında aynı dörtlüyü zorunlu kılar.
      name: 'location', label: t('ats.requisition.location'), type: 'asyncSelect', grid: 6, required: true,
      onSearch: async (q) => {
        const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
        const res = await api.get('/v1/locations', { params });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        return items.map((l) => ({ id: l.name, name: l.name }));
      },
      onGetById: async (id) => ({ id, name: id || '' }),
    },
    { name: 'contractType', label: t('ats.requisition.contractType.label'), type: 'select', grid: 6, required: true, options: contractOptions, idKey: 'id', labelKey: 'name' },
    { name: 'employmentType', label: t('ats.requisition.employmentType.label'), type: 'select', grid: 6, required: true, options: employmentOptions, idKey: 'id', labelKey: 'name' },
    { name: 'remoteType', label: t('ats.requisition.remoteType.label'), type: 'select', grid: 6, required: true, options: remoteOptions, idKey: 'id', labelKey: 'name' },
    {
      name: 'salaryCurrency', label: t('ats.requisition.salaryCurrency'), type: 'select', grid: 6, options: currencyOptions, idKey: 'id', labelKey: 'name',
      derives: ['minSalary', 'maxSalary'],
      deriveFields: deriveCurrencyBand,
    },
    {
      name: 'minSalary', label: t('ats.requisition.minSalary'), type: 'number', grid: 6, min: 0, step: '0.01',
      description: (fd) => (fd.salaryBandHint
        ? t('ats.requisition.bandHint', {
          min: Number(fd.salaryBandHint.min).toLocaleString(i18nLocale),
          max: Number(fd.salaryBandHint.max).toLocaleString(i18nLocale),
          currency: fd.salaryBandHint.currency,
        })
        : undefined),
    },
    { name: 'maxSalary', label: t('ats.requisition.maxSalary'), type: 'number', grid: 6, min: 0, step: '0.01' },
    { name: 'justificationText', label: t('ats.requisition.justification'), type: 'textarea', grid: 12 },
  ].filter((f) => {
    if (f.name === 'hrBpUserId') return writable && !ownerLocked;
    if (f.name === 'hiringManagerUserId') return !ownerLocked;
    if (f.name === 'hiringManagerIsYou') return ownerLocked;
    return true;
  }),
  [t, i18nLocale, modeOptions, positionSearch, positionGetById, derivePositionAndBand, deriveTitleBand, deriveParentBand, deriveCurrencyBand, contractOptions, employmentOptions,
    remoteOptions, currencyOptions, userAsyncSelect, hrBpSearch, noteHiringManager, writable, ownerLocked]);

  const buildPayload = useCallback(
    (formData, opts = {}) => buildRequisitionPayload(formData, { ...opts, ownerLocked }),
    [ownerLocked],
  );

  return { objectSchema, buildPayload, noteHiringManager, buildPrefill };
}
